import { useDeferredValue, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Search } from 'lucide-react';
import clsx from 'clsx';
import {
  HTTP_STATUSES,
  RFC_BASE,
  searchStatuses,
  type StatusCategory,
  type StatusDef,
} from '../lib/httpStatus';
import { ToolHeader } from '../components/ToolHeader';

const ALL_CATEGORIES: ReadonlyArray<{ id: StatusCategory; label: string; tone: string }> = [
  { id: '1xx', label: '1xx Informational', tone: 'text-sky-500' },
  { id: '2xx', label: '2xx Success', tone: 'text-emerald-500' },
  { id: '3xx', label: '3xx Redirect', tone: 'text-cyan-500' },
  { id: '4xx', label: '4xx Client Error', tone: 'text-amber-500' },
  { id: '5xx', label: '5xx Server Error', tone: 'text-rose-500' },
];

/**
 * `/tools/network/http-status` — searchable HTTP status code dictionary.
 *
 * UX hinges on a single full-text search box + category checkboxes.
 * Results are grouped by category so the user can scan related codes
 * (e.g. "all the redirects") without re-searching.
 */
export default function HttpStatusTool(): JSX.Element {
  const [query, setQuery] = useState('');
  const [activeCats, setActiveCats] = useState<Set<StatusCategory>>(
    () => new Set(ALL_CATEGORIES.map((c) => c.id)),
  );

  const deferredQuery = useDeferredValue(query);
  const matches = useMemo(
    () => searchStatuses(deferredQuery, { categories: activeCats }),
    [deferredQuery, activeCats],
  );

  // When the user typed a number that resolves to a single match, auto-expand it.
  const initiallyExpanded = useMemo(() => {
    if (/^\d+$/.test(deferredQuery.trim()) && matches.length === 1) {
      return new Set([matches[0].code]);
    }
    return new Set<number>();
  }, [deferredQuery, matches]);

  const grouped = useMemo(() => groupByCategory(matches), [matches]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="http-status"
        category="network"
        description={`所有 IETF 标准状态码的中英文释义、用法说明与 RFC 链接。共 ${HTTP_STATUSES.length} 个；扩展状态码（Cloudflare / nginx）暂不收录。`}
      />

      <section className="card-pad flex flex-col gap-2">
        <label className="flex items-center gap-2 rounded border border-line px-2 py-1">
          <Search className="h-3.5 w-3.5 text-ink-dim" />
          <input
            className="flex-1 bg-transparent text-sm outline-none"
            placeholder="数字 (404) / 英文 (unauth) / 中文 (未授权) 都可"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            spellCheck={false}
          />
        </label>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {ALL_CATEGORIES.map((c) => (
            <label key={c.id} className="inline-flex items-center gap-1 text-ink-muted">
              <input
                type="checkbox"
                checked={activeCats.has(c.id)}
                onChange={(e) => {
                  setActiveCats((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(c.id);
                    else next.delete(c.id);
                    return next;
                  });
                }}
              />
              <span className={c.tone}>{c.label}</span>
            </label>
          ))}
        </div>
      </section>

      {matches.length === 0 ? (
        <p className="card-pad text-[12px] text-ink-dim">— 未找到匹配的状态码 —</p>
      ) : (
        ALL_CATEGORIES.map((cat) => {
          const list = grouped[cat.id];
          if (!list || list.length === 0) return null;
          return (
            <section key={cat.id} className="card-pad flex flex-col gap-1">
              <header className={clsx('text-xs font-semibold', cat.tone)}>
                {cat.label} <span className="text-ink-dim">（{list.length}）</span>
              </header>
              <ul className="flex flex-col divide-y divide-line/40">
                {list.map((s) => (
                  <StatusItem
                    key={s.code}
                    status={s}
                    defaultOpen={initiallyExpanded.has(s.code)}
                  />
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}

function StatusItem({
  status,
  defaultOpen,
}: {
  status: StatusDef;
  defaultOpen: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const Caret = open ? ChevronDown : ChevronRight;
  return (
    <li className="py-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 text-left text-[13px] hover:text-accent"
      >
        <Caret className="h-3 w-3 shrink-0 text-ink-dim" />
        <span className="font-mono font-semibold text-ink">{status.code}</span>
        <span className="text-ink">{status.name}</span>
        <span className="text-ink-dim">{status.nameZh}</span>
      </button>
      {open && (
        <div className="mt-1 pl-5 text-[12.5px] text-ink-muted leading-relaxed">
          <p>{status.description}</p>
          {status.rfc && (
            <p className="mt-1 inline-flex items-center gap-1 text-accent">
              <a
                href={`${RFC_BASE}${status.rfc}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {status.rfc}
              </a>
              <ExternalLink className="h-3 w-3" />
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function groupByCategory(list: StatusDef[]): Record<StatusCategory, StatusDef[]> {
  const out: Record<StatusCategory, StatusDef[]> = {
    '1xx': [],
    '2xx': [],
    '3xx': [],
    '4xx': [],
    '5xx': [],
  };
  for (const s of list) out[s.category].push(s);
  return out;
}
